import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import VehicleType from '../components/Filters/VehicleType';
import OfferTypeSelector from '../components/Filters/OfferType/OfferType';
import BodyStyle from '../components/Filters/BodyStyle/BodyStyleList';
import ModelFamilySelector from '../components/Filters/ModelFamily/ModelFamilySelector';
import ModelYearSelector from '../components/Filters/ModelYear/ModelYearSelector';
import useFilterOptions from './useFilterOptions';
import { useAvailableFilterOptions } from './useAvailableFilterOptions';
import { CA_FILTERS, ExposedFiltersTypes, US_FILTERS } from '../constants/initialFilters';
import { selectFilters } from '../reducers/filtersReducer';
import OfferRegion from '../components/Filters/OfferRegion';

const useFiltersList = (
  openedFiltersList: ExposedFiltersTypes,
  isCaMarket: boolean,
  handleAddFilter: (filterType: string, filterValue: string, isChecked?: boolean) => void,
  handleExposeFilter: (filterType: string) => void,
) => {
  const {
    offerTypeOptions,
    bodyTypeOptions,
    modelFamilyOptions,
    trimLineOptions,
    modelYearOptions,
    vehicleTypeOptions,
    offerRegionOptions,
  } = useFilterOptions();

  const {
    availableOfferTypeOptions,
    availableBodyTypeOptions,
    availableModelFamilyOptions,
    availableOfferRegionOptions,
    availableTrimLineOptions,
    availableModelYearOptions,
    totalAvailableOffers,
  } = useAvailableFilterOptions();

  const { confirmedFilters } = useSelector(selectFilters);

  const vehicleTypeFilter = useMemo(() => {
    return {
      value: 'vehicleType',
      Component: (
        <VehicleType
          filterType="vehicleType"
          vehicleTypeOptions={vehicleTypeOptions}
          openedFiltersList={openedFiltersList}
          availableFilters={availableOfferTypeOptions}
          confirmedFilters={confirmedFilters}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    availableOfferTypeOptions,
    confirmedFilters,
    handleAddFilter,
    handleExposeFilter,
    openedFiltersList,
    vehicleTypeOptions,
  ]);

  const offerTypeFilter = useMemo(() => {
    return {
      value: 'offerType',
      Component: (
        <OfferTypeSelector
          filterType="offerType"
          offerTypeOptions={offerTypeOptions}
          openedFiltersList={openedFiltersList}
          availableFilters={availableOfferTypeOptions}
          confirmedFilters={confirmedFilters}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    availableOfferTypeOptions,
    confirmedFilters,
    handleAddFilter,
    handleExposeFilter,
    offerTypeOptions,
    openedFiltersList,
  ]);

  const offerRegionFilter = useMemo(() => {
    return {
      value: 'offerRegion',
      Component: (
        <OfferRegion
          filterType="offerRegion"
          offerRegionOptions={offerRegionOptions}
          openedFiltersList={openedFiltersList}
          availableFilters={availableOfferRegionOptions}
          confirmedFilters={confirmedFilters}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    availableOfferRegionOptions,
    confirmedFilters,
    handleAddFilter,
    handleExposeFilter,
    offerRegionOptions,
    openedFiltersList,
  ]);

  const bodyTypeFilter = useMemo(() => {
    return {
      value: 'bodyType',
      Component: (
        <BodyStyle
          filterType="bodyType"
          bodyTypeOptions={bodyTypeOptions}
          availableFilters={availableBodyTypeOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    availableBodyTypeOptions,
    bodyTypeOptions,
    confirmedFilters,
    handleAddFilter,
    handleExposeFilter,
    openedFiltersList,
  ]);

  const modelFamilyFilter = useMemo(() => {
    return {
      value: 'modelFamily',
      Component: (
        <ModelFamilySelector
          filterType="modelFamily"
          modelFamilyOptions={modelFamilyOptions}
          availableFilters={availableModelFamilyOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    availableModelFamilyOptions,
    confirmedFilters,
    handleAddFilter,
    handleExposeFilter,
    modelFamilyOptions,
    openedFiltersList,
  ]);

  const modelYearFilter = useMemo(() => {
    return {
      value: 'modelYear',
      Component: (
        <ModelYearSelector
          filterType="modelYear"
          modelYearOptions={modelYearOptions}
          availableFilters={availableModelYearOptions}
          confirmedFilters={confirmedFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    availableModelYearOptions,
    confirmedFilters,
    handleAddFilter,
    handleExposeFilter,
    modelYearOptions,
    openedFiltersList,
  ]);

  const fullListOfFilters = useMemo(() => {
    return [
      vehicleTypeFilter,
      offerTypeFilter,
      bodyTypeFilter,
      modelFamilyFilter,
      modelYearFilter,
      offerRegionFilter,
    ];
  }, [
    bodyTypeFilter,
    modelFamilyFilter,
    modelYearFilter,
    offerTypeFilter,
    vehicleTypeFilter,
    offerRegionFilter,
  ]);

  const listOfFiltersToRender = useMemo(() => {
    if (isCaMarket)
      return fullListOfFilters
        .filter((vehicleFilter) => CA_FILTERS.includes(vehicleFilter.value))
        .sort((a, b) => CA_FILTERS.indexOf(a.value) - CA_FILTERS.indexOf(b.value));
    return fullListOfFilters
      .filter((vehicleFilter) => US_FILTERS.includes(vehicleFilter.value))
      .sort((a, b) => US_FILTERS.indexOf(a.value) - US_FILTERS.indexOf(b.value));
  }, [fullListOfFilters, isCaMarket]);

  return listOfFiltersToRender;
};

export default useFiltersList;
